import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { getShouldUsingCredentialServiceEndpoint, shouldUseDsoStructure } from '../growthbook';
import { store } from '../store/configureStore';
import { apiMethodsConst } from './methods';
import { getCommonHeaders } from './utils';
import { attachRetry } from './interceptors/attachRetry';
import { attachCloudflareChallenge } from './interceptors/cloudflareChallenge';
import { LOGOUT_ACTION } from '../actions';
import { RemoteConfigUtils } from '../utils/RemoteConfigUtils';

export const tempMeeApi = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  withCredentials: true,
});

attachRetry(tempMeeApi);
attachCloudflareChallenge(tempMeeApi);

tempMeeApi.interceptors.request.use(async (config) => ({
  ...config,
  headers: {
    ...(await getCommonHeaders()),
    ...{ 'x-correlation-id': uuid(), platform: 'web' },
    ...config.headers,
  },
}));

export async function getAuthHeaders() {
  // Refresh the token if it is expired or missing
  if (!store.getState().auth.expirationDate || store.getState().auth.expirationDate < new Date()) {
    const response = await authRefreshRequest(localStorage.getItem('refresh_token_dental'));
    store.dispatch({
      type: apiMethodsConst.AUTH_REFRESH_REQUEST,
      payload: response,
    });
  }

  /* x-office */
  const { user, selectedOfficeId } = store.getState().user;

  let xOffice = undefined;
  if (shouldUseDsoStructure() && selectedOfficeId) {
    xOffice = selectedOfficeId;
  } else if (!shouldUseDsoStructure() && user && selectedOfficeId && selectedOfficeId !== user.id) {
    xOffice = selectedOfficeId;
  }

  /* Access Token */
  const { accessToken } = store.getState().auth;

  const headers = {
    Authorization: accessToken,
  };

  if (xOffice) {
    headers['x-office'] = xOffice;
  }

  return headers;
}

async function getAuthPPHeaders() {
  if (!store.getState().auth.expirationDate || store.getState().auth.expirationDate < new Date()) {
    const response = await authRefreshRequest(localStorage.getItem('refresh_token_dental'));
    store.dispatch({
      type: apiMethodsConst.AUTH_REFRESH_REQUEST,
      payload: response,
    });
  }
  const { accessToken } = store.getState().auth;
  return {
    Authorization: accessToken,
  };
}

const getHeaders = async (url) => {
  const isAuthRequest =
    url.includes('/auth/') ||
    url.includes('/users/password') ||
    url === '/auth-service/users' ||
    url === '/auth-service/sessions' ||
    url === '/auth-service/sessions/refresh' ||
    url === '/auth-service/users/password';

  const isExcludeAuthRequest =
    url === '/auth/me' || url === '/auth/logout' || url === '/auth-service/users';

  if (!isAuthRequest || isExcludeAuthRequest) {
    return getAuthHeaders(url);
  }

  return {};
};

async function callApi(method, url, body = null, params, extraHeaders, config = {}) {
  // get store out of the scope of react nad get headers
  const authHeaders = await getHeaders(url);
  const data = method === 'GET' ? { params: body } : { data: body };
  const response = await tempMeeApi({
    method,
    url,
    ...data,
    headers: { ...authHeaders, ...extraHeaders },
    params,
    ...config,
  });
  return response.data;
}

async function callAuthServiceApi(method, url, body = null, params, extraHeaders, config) {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  const response = await callApi(
    method,
    `/auth-service${normalizedUrl}`,
    body,
    params,
    extraHeaders,
    config,
  );

  return response.data;
}

async function callApiReturnParam(method, url, body = null, params) {
  // get store out of the scope of react nad get headers
  const isAuthRequest = url.includes('/auth/') && url !== '/auth/me' && url !== '/auth/logout';
  const authHeaders = !isAuthRequest ? await getAuthHeaders() : {};
  const data = method === 'GET' ? { params: body } : { data: body };
  await tempMeeApi({
    method,
    url,
    ...data,
    headers: authHeaders,
    params,
  });
  return body;
}

async function callApiWithValidate(method, url, params, body = undefined) {
  // get store out of the scope of react nad get headers
  const headers = await getAuthHeaders();
  const response = await tempMeeApi({
    method,
    url,
    params,
    headers,
    validateStatus: (status) => (status >= 200 && status < 300) || status === 400,
    data: body,
  });

  return response;
}

async function callPPApi(method, url, body = null, params, isPostedApplicationId) {
  // get store out of the scope of react nad get headers
  const authHeaders = await getAuthPPHeaders();
  const data = method === 'GET' ? { params: body } : { data: body };
  const response = await tempMeeApi({
    method,
    url,
    ...data,
    headers: authHeaders,
    params,
  });
  return isPostedApplicationId
    ? { applicationId: isPostedApplicationId, lastApplicationAction: response.data }
    : response.data;
}

const signInRequest = async ({ email, password, turnstileToken }) => {
  const data = { email, password };

  if (turnstileToken) {
    data.turnstileToken = turnstileToken;
  }

  const response = await callAuthServiceApi('POST', '/sessions', data);

  const payload = {
    access_token: response.accessToken,
    refresh_token: response.refreshToken,
    expires_in: response.expiresIn,
  };

  return payload;
};

const authMeRequest = () => callApi('GET', '/auth/me', {}, undefined, { 'x-office': undefined });
const userCredentialsRequest = () => callApi('GET', '/credential-service/user/credentials');

const authSignUpRequest = async ({ email, password, referral_id: referralId }) => {
  const response = await callAuthServiceApi('POST', '/users', {
    email,
    password,
    source: 'web-portal',
    referralID: referralId,
  });

  const payload = {
    access_token: response.accessToken,
    refresh_token: response.refreshToken,
    expires_in: response.expiresIn,
  };

  await callAuthServiceApi('PATCH', '/users', { userType: 'DNT' }, undefined, {
    Authorization: payload.access_token,
  });

  return payload;
};

const authLogoutRequest = () => {
  return callAuthServiceApi('DELETE', '/sessions', {
    refreshToken: localStorage.getItem('refresh_token_dental'),
  });
};

// Shared state for refresh request deduplication
let refreshPromise = null;
let lastRefreshTime = 0;
let lastRefreshResponse = null;

const REFRESH_MAX_RETRIES = 3;
const REFRESH_RETRY_DELAY_MS = 2000;
const MIN_REFRESH_INTERVAL_MS = 10000; // Don't refresh again within 10 seconds

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const executeRefreshWithRetry = async (token) => {
  let lastError;

  for (let attempt = 1; attempt <= REFRESH_MAX_RETRIES; attempt++) {
    try {
      const timeoutMs = RemoteConfigUtils.getApiDefaultTimeoutMs();
      const response = await callAuthServiceApi(
        'POST',
        '/sessions/refresh',
        {
          refreshToken: token,
        },
        undefined,
        undefined,
        { ...(timeoutMs && { timeout: timeoutMs }) },
      );

      return {
        access_token: response.accessToken,
        refresh_token: response.refreshToken,
        expires_in: response.expiresIn,
      };
    } catch (error) {
      lastError = error;

      if (attempt < REFRESH_MAX_RETRIES) {
        await delay(REFRESH_RETRY_DELAY_MS);
      }
    }
  }

  // All retries failed - logout
  store.dispatch({
    type: LOGOUT_ACTION,
  });
  setTimeout(() => {
    store.dispatch({
      type: 'REFRESH_TOKEN_FAILURE',
      payload: lastError,
    });
  }, 1000);

  return {};
};

const authRefreshRequest = async (token) => {
  if (!token) {
    return {};
  }

  // If refresh already in progress, wait for it (deduplication)
  if (refreshPromise) {
    return refreshPromise;
  }

  // If we refreshed recently, return the cached response (throttle)
  const now = Date.now();
  if (lastRefreshResponse?.access_token && now - lastRefreshTime < MIN_REFRESH_INTERVAL_MS) {
    return lastRefreshResponse;
  }

  // Start new refresh with retry logic
  refreshPromise = executeRefreshWithRetry(token);

  try {
    const result = await refreshPromise;
    // Cache successful response
    if (result?.access_token) {
      lastRefreshTime = Date.now();
      lastRefreshResponse = result;
    }
    return result;
  } finally {
    refreshPromise = null;
  }
};

const forgotPasswordSendCodeRequest = (data) => {
  return callAuthServiceApi('POST', '/otp', {
    otpType: 'reset_password',
    identifier: data?.email || data?.phone,
  });
};

const forgotPasswordResetRequest = async ({ data, identifier }) => {
  let requestToken = localStorage.getItem('requestToken');
  if (!requestToken) {
    const response = await callAuthServiceApi('POST', '/otp/verification', {
      identifier,
      otpType: 'reset_password',
      otpCode: data?.code,
    });
    requestToken = response?.requestToken;
    localStorage.setItem('requestToken', requestToken);
  }

  return callAuthServiceApi('PATCH', '/users/password', {
    identifier,
    requestToken: requestToken,
    newPassword: data?.password,
  });
};

// Ignore contact_number from the request
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const editUserRequest = ({ contact_number, ...data }) => callApi('PATCH', '/users', data);

const addAddressRequest = (place_id) =>
  callApi('POST', '/user/address', { place_id, distance: 25 });
const addOfficeProductivityRequest = (productivity) =>
  callApi('PATCH', '/users/office-productivity', { productivity });

const sendSMSCodeRequest = (phone) => {
  return callAuthServiceApi('POST', '/otp', {
    otpType: 'change_phone',
    identifier: phone,
  });
};

const phoneVerificationRequest = async ({ code, phone }) => {
  const response = await callAuthServiceApi('POST', '/otp/verification', {
    identifier: phone,
    otpType: 'change_phone',
    otpCode: code,
  });

  return callAuthServiceApi('PATCH', '/users/phone', {
    requestToken: response?.requestToken,
    newPhone: phone,
  });
};

const deleteAddressesRequest = () => callApi('DELETE', '/user/address');
const blockProfessionalRequest = ({ userId }) =>
  callApi('POST', `/users/${userId}/addBlocked`, { userId });
const feedbackRequest = () => callApi('GET', '/feedback/required');
const feedbackSaveRequest = ({ data, jobId }) => callApi('POST', `/review/job/${jobId}`, data);
const sendProcedureEndorsementRequest = ({ endorsement, userId }) =>
  callApi('POST', `/credential-service/users/${userId}/credentials/reviews`, endorsement);
const fetchUserProcedureEndorsementRequest = ({ candidateId, profession }) =>
  callApi('GET', `/credential-service/users/${candidateId}/credentials`, null, { profession });

const saveNpsRequest = (score) => callApi('POST', '/nps-review', { score });
const saveTempMeeFeedbackRequest = ({ rating, ratingDesc, message }) =>
  callApi('POST', '/feedback/tempmee', { message, rating, ratingDesc });
const getAllProfessionsRequest = () => callApi('GET', '/professions');
const getAllSpecializationsRequest = () => callApi('GET', '/specializations');

/**
 * Getting user's education. Contains user's specialties, procedures, insurances and softwares
 *
 * Use {@link getShouldUsingCredentialServiceEndpoint} feature flag for switching between old and new endpoints.
 *
 * @param {UserProfession} profession
 * @returns {Promise<any>}
 */
const getUserEducationRequest = ({ profession }) => {
  const professionType = profession || 'RDH';

  if (getShouldUsingCredentialServiceEndpoint()) {
    return callApi(
      'GET',
      `/credential-service/credentials?userType=HYG&profession=${professionType}`,
    );
  }

  // replaced from wrong access from 'DNT' to 'HYG' and eliminates `.DNT` key access
  // to use `.[profession]` key access
  return callApi('GET', `/users/education?userType=HYG&profession=${professionType}`);
};

/**
 * Getting office's education. Contains user's specialties, procedures, insurances and softwares
 *
 * Use {@link getShouldUsingCredentialServiceEndpoint} feature flag for switching between old and new endpoints.
 *
 * @returns {Promise<any>}
 */
const getOfficeEducationRequest = () => {
  if (getShouldUsingCredentialServiceEndpoint()) {
    return callApi('GET', '/credential-service/credentials?userType=DNT');
  }

  // profession need to be declared otherwise returns error
  return callApi('GET', `/users/education?userType=DNT&profession=RDH`);
};
const updateUserEducationRequest = (data) =>
  callApi('PATCH', '/credential-service/user/credentials', data);
const getAllSoftwaresRequest = () => callApi('GET', '/softwares');
const acceptTermsRequest = () => callApi('POST', '/user/tos/gotu');
const getTermsAndConditionsForStaffRequest = () =>
  callApi('GET', '/office-service/staffs/gotu-terms');
const acceptTermsAndConditionsForStaffRequest = () =>
  callApi('POST', '/office-service/staffs/gotu-terms');
const acceptPSATermsRequest = () => callApi('POST', '/user/tos/psa');
const uploadAvatarRequest = (data) =>
  callApi('PATCH', '/user/avatar', data, null, { 'Content-Type': 'multipart/form-data' });

// payment requests
const getAllPaymentsRequest = (data) =>
  callApi(
    'GET',
    '/payment/account/payment-method/',
    undefined,
    undefined,
    data?.officeId
      ? {
          'x-office': data.officeId,
        }
      : undefined,
  );
const addPaymentRequest = (data) => callApi('POST', '/payment/account/payment-method/', data);
const deletePaymentRequest = (paymentId) =>
  callApi('DELETE', `/payment/account/payment-method/${paymentId}`);
const selectPaymentRequest = (paymentId) =>
  callApi('PATCH', `/payment/account/payment-method/${paymentId}`, {});
const verifyBankRequest = ({ paymentId, data }) =>
  callApi('POST', `/paymentMethods/${paymentId}/verify`, { amounts: data });
const createPlaidToken = () => callApi('POST', '/payment/account/plaid/', {});
const submitPlaidToken = (body) => callApi('POST', '/payment/account/plaid/connect', body);
// job
const createJobRequest = (data) => callApi('POST', '/jobs', data);
const fetchJobRequest = (jobId) => callApi('GET', `/jobs/${jobId}`);
const fetchJobCandidatesRequest = ({ jobId, params }) =>
  callApi('GET', `/jobs/${jobId}/candidates`, {}, params);
const fetchCandidateOvertimeRequest = ({ jobId, candidateId }) =>
  callApi('GET', `jobs/${jobId}/candidate/${candidateId}`);
const getUserOvertimeLimitsRequest = () => callApi('GET', `/users/overtime-limits`);
const editJobRequest = ({ jobId, jobData }) => callApi('PATCH', `/jobs/${jobId}`, jobData);
const getAllJobsRequest = (data) => callApi('GET', '/jobs/count-and-dates', {}, data);
const getJobsRequestByStatusDate = ({ date, status, page = 1, limit = 10 }) =>
  callApi(
    'GET',
    '/jobs',
    {},
    date
      ? {
          date,
          sortField: 'job_datetime',
          sortOrder: 'asc',
          page,
          limit,
        }
      : {
          status,
          sortField: 'job_datetime',
          sortOrder: status === 'all' || status === 'past' ? 'desc' : 'asc',
          page,
          limit,
        },
  );
const getLastPostedJobsRequest = () =>
  callApi(
    'GET',
    '/jobs',
    {},
    {
      sortField: 'createdAt',
      sortOrder: 'desc',
      status: 'all',
      limit: 1,
      page: 1,
    },
  );
const fetchJobsWithTransactions = (data) => callApi('GET', '/jobs-with-transactions', {}, data);
const cancelJobRequest = ({ jobId }) => callApi('POST', `/jobs/${jobId}/cancel`);
const confirmJobRequest = ({ jobId, data, counterOfferId }) =>
  callApi('POST', `/jobs/${jobId}/confirm/${counterOfferId}`, data);
const reportIncidentRequest = ({ jobId, type, repostMaxStartTime }) =>
  callApi('POST', `/jobs/${jobId}/incidents`, { type, repostMaxStartTime });
const multidayJobPostingRequest = (data) =>
  callApi('POST', '/multi-day', data.jobInfo, {}, { 'x-office': data.officeId });
const fetchOpenJobRequest = (data) => callApi('GET', 'jobs/dashboard/open', {}, data);
const fetchScheduledJobsRequest = (data) => callApi('GET', 'jobs/dashboard/filled', {}, data);
const fetchActionRequiredJobsRequest = (data) =>
  callApi('GET', 'jobs/dashboard/action-required', {}, data);
const fetchTodayJobsRequest = (data) => callApi('GET', 'jobs/dashboard/today', {}, data);
const fetchExpiredCounterOffersRequest = (jobId) =>
  callApi('GET', `offers/${jobId}/declined-expired`);

const declineCounterOffer = ({ jobId, counterOfferId, cancellation_reason }) =>
  callApi('DELETE', `jobs/${jobId}/offers/${counterOfferId}`, { cancellation_reason });
const adjustHoursRequest = ({ jobId, data }) => callApi('POST', `jobs/${jobId}/adjust`, data);
const fetchAverageRatingRequest = () => callApi('GET', 'jobs/average-rate');
const fetchCancellationReasonsRequest = () => callApi('GET', 'cancellation-reasons');
const setCancellationReasonsRequest = ({ jobId, data }) =>
  callApi('POST', `/jobs/${jobId}/cancellationFeedback`, data);

const getJobsRequest = ({ limit, page, professionalId, sortField, sortOrder, status, officeId }) =>
  callApi(
    'GET',
    '/jobs',
    {},
    {
      limit: limit || 10,
      page: page || 1,
      professionalId,
      sortField,
      sortOrder,
      status,
    },
    {
      'x-office': officeId || undefined,
    },
  );

const getEligibleJobsRequest = ({ professionalId, page, limit, status, xOfficeId, profession }) =>
  callApi(
    'GET',
    `/offices/professionals/${professionalId}/eligible-jobs`,
    {},
    {
      page: page || 1,
      limit: limit || 15,
      profession,
      status,
    },
    {
      'x-office': xOfficeId || undefined,
    },
  );

// alerts
const getAlertsRequest = (state) => callApi('GET', '/alerts', {}, state);
const deleteAlertsRequest = () => callApi('DELETE', '/alerts/clear');
const deleteOneAlertRequest = (alertId) => callApi('DELETE', `/alerts/${alertId}/read`);
// messages
const getMessagesRequest = (state) => callApi('GET', '/conversations', {}, state);
const getMessagesByJobIdRequest = (jobId) => callApi('GET', `/jobs/${jobId}/messages`);
const postMessageRequest = ({ jobId, message }) =>
  callApi('POST', `/jobs/${jobId}/messages`, { text: message });
const inviteAdminRequest = (jobId) => callApi('POST', `/jobs/${jobId}/invite-admin`, {});
// DSO
const getSelectedOfficeProfile = (officeId) =>
  callApi('GET', '/auth/me', undefined, undefined, officeId ? { 'x-office': officeId } : undefined);
const getDSOOfficeByIdRequest = ({ officeId }) =>
  callApi('GET', `/office-service/offices/${officeId}`);

// receipts
const fetchReceiptsRequest = (state) => callApi('GET', '/receipts/dashboard', {}, state);
const fetchReceiptsReportRequest = (state) => callApi('GET', '/receipts/report', {}, state);
const fetchReceiptsV2Request = (payload) =>
  callApi(
    'POST',
    '/v2/receipts/dashboard',
    { officeId: payload?.officeId },
    {
      start: payload?.startsAt || undefined,
      end: payload?.endsAt || undefined,
      page: payload?.page || 1,
      limit: payload?.limit || 10,
      sortBy: payload?.sortBy || undefined,
      sortOrder: payload?.sortOrder || undefined,
    },
  );

const fetchReceiptsCSVReportV2Request = (payload) =>
  callApi(
    'POST',
    '/v2/receipts/report',
    {
      officeId: payload?.officeIds,
    },
    {
      start: payload?.startsAt || undefined,
      end: payload?.endsAt || undefined,
      sortBy: payload?.sortBy || undefined,
      sortOrder: payload?.sortOrder || undefined,
    },
  );

// invoice
const fetchInvoicesRequest = (state) => callApi('GET', '/invoices', {}, state);

const requestPaymentReport = ({ email, dateRange, notes }) =>
  callApi('POST', '/receipts/request/report', { email, dateRange, notes });

// PP
const ppbPostingRequest = (data) => callApi('POST', '/jobs/permanent', data);

const fetchAllPPJobsRequest = () => callPPApi('GET', '/pp/jobs?page=1&limit=10');

const fetchPPApplicationsRequest = async ({ jobId }) => callPPApi('GET', `/pp/jobs/${jobId}`);

const fetchPPApplicationByIdRequest = ({ jobId, applicationId }) =>
  callPPApi('GET', `/pp/jobs/${jobId}/applications/${applicationId}`);
const postPPActionnRequest = ({ jobId, actionType, applicationId, notes }) =>
  callPPApi(
    'POST',
    `/pp/jobs/${jobId}/applications/${applicationId}/action`,
    { action: actionType, notes },
    {},
    applicationId,
  );

// adjustments
const fetchAdjustmentsRequest = () => callApi('GET', '/adjustments/required');
const changeStatusAdjustmentsRequest = ({ jobId, adjustmentId, status, disputeReason }) =>
  callApi('PATCH', `/jobs/${jobId}/adjust/${adjustmentId}`, { status, disputeReason });

// locum tenens dentist
const createLocumTenensDentistRequest = (text) => callApi('POST', '/locum-tenens', { text });
// notifications
const fetchNotificationSettingsRequest = () =>
  callApi('GET', '/notification-sender/notification-settings');
const updateNotificationSettingsRequest = (notificationSettings) =>
  callApi('PATCH', '/notification-sender/notification-settings', notificationSettings);

// favorites
const fetchFavoritesProfessionalRequest = (params) =>
  callApi('GET', '/favorite-professionals', {}, params || {});
const deleteFavoriteProfessionalRequest = (id) =>
  callApi('DELETE', `/favorite-professionals/${id}`);
const addFavoriteProfessionalRequest = (id) =>
  callApiReturnParam('PATCH', '/favorite-professionals', { userIds: [id] });
const inviteFavoriteToJobRequest = ({ jobId, userIds, officeId }) =>
  callApi('POST', `/jobs/${jobId}/invites`, { userIds }, {}, { 'x-office': officeId });
const inviteProfessionalsToJobsRequest = ({ jobIds, professionalIds, officeId }) =>
  callApi('POST', `/jobs/invites`, { jobIds, professionalIds }, {}, { 'x-office': officeId });

const fetchRecentProfessionalsRequest = (params) =>
  callApi('GET', '/users/recent-professionals', {}, params || {});
const fetchOpenJobsForRecentProfessionalRequest = ({ professionalId, officeId, ...params }) =>
  callApi(
    'GET',
    `/offer-discovery/professionals/${professionalId}/offers`,
    {},
    { ...params },
    officeId
      ? {
          'x-office': officeId,
        }
      : {},
  );

const fetchRecommendedProfessionalsRequest = (params) =>
  callApi('GET', '/offer-discovery/professionals/recommended', {}, params || {});

// invites
const fetchOfficeJobInvitesRequest = (params) => callApi('GET', `/jobs/invites`, {}, params || {});

// partnership code
const fetchPartnershipOrganizationsRequest = () => callApi('GET', '/partnership-code/organization');
const verifyPartnershipCodeRequest = (data) => callApi('PATCH', '/users', data);

// metadata
const fetchMetadataRequest = () => callApi('GET', '/metadata');

// worker type
const saveWorkerClassificationRequest = (type) =>
  callApi('PATCH', '/users/worker-classification', type);
const saveChildOfficeWorkerClassification = (params) =>
  callApi('PATCH', `/users/${params.id}/worker-classification`, { type: params.workerType });
const saveStatusChildOfficeWorkerClassification = (params) =>
  callApi('POST', `/users/worker-classification/${params.id}`, { status: params.status });
const getStateWorkerClassificationRequest = () =>
  callApi('GET', '/users/state-worker-classification');
const getWorkerClassificationManagement = () => callApi('GET', '/users/worker-classification');

// Office Review
const getOfficeReviewInfoRequest = ({ userId }) => callApi('GET', `/users/${userId}/review-info`);

// Child Offices
const getChildOfficesRequest = () => callApi('GET', '/offices');

// Referral
const saveGiftEmailRequest = (email) =>
  callApi('PATCH', '/users/gift-email', { gift_email: email });
const getReferralRequest = (type) => callApiWithValidate('GET', `referral/${type}`);
const createReferralRequest = (type) => callApi('POST', 'referral', { type }, { type });
const putReferralLinkRequest = (referralId, referralLink, type) =>
  callApi('PUT', `referral/${referralId}/url`, { id: referralId, url: referralLink }, { type });
const getReferralList = () =>
  callApi('GET', '/v2/referral-statuses', undefined, { isCancelled: false });
const getReferralSummary = () => callApi('GET', '/payment/referral-payout/summary');
const getReferralPayouts = () => callApi('GET', '/payment/referral-payout');
const confirmReferralPayout = (referralId) =>
  callApi('PATCH', `/payment/referral-payout/${referralId}`);
const linkReferralAccount = (link) =>
  callApiWithValidate('POST', `/referral-link`, undefined, { link });

// Professional Profile
const fetchProfessionalProfile = (data) => {
  const params = {};
  if (data?.jobProfession) params.jobProfession = data.jobProfession;
  if (data?.jobSubProfession) params.jobSubProfession = data.jobSubProfession;
  if (data?.jobState) params.jobState = data.jobState;

  return callApi(
    'GET',
    `/professionals/${data.userId}/profile`,
    undefined,
    Object.keys(params).length > 0 ? params : undefined,
    data?.officeId
      ? {
          'x-office': data.officeId,
        }
      : {},
  );
};

export const apiMethods = {
  signInRequest,
  authMeRequest,
  userCredentialsRequest,
  authSignUpRequest,
  authLogoutRequest,
  forgotPasswordSendCodeRequest,
  forgotPasswordResetRequest,
  editUserRequest,
  addAddressRequest,
  addOfficeProductivityRequest,
  phoneVerificationRequest,
  sendSMSCodeRequest,
  deleteAddressesRequest,
  blockProfessionalRequest,
  feedbackRequest,
  authRefreshRequest,
  feedbackSaveRequest,
  sendProcedureEndorsementRequest,
  fetchUserProcedureEndorsementRequest,
  saveNpsRequest,
  saveTempMeeFeedbackRequest,
  acceptTermsRequest,
  getTermsAndConditionsForStaffRequest,
  acceptTermsAndConditionsForStaffRequest,
  acceptPSATermsRequest,
  uploadAvatarRequest,
  // paymnet
  selectPaymentRequest,
  deletePaymentRequest,
  addPaymentRequest,
  getAllPaymentsRequest,
  verifyBankRequest,
  createPlaidToken,
  submitPlaidToken,
  // job
  fetchAverageRatingRequest,
  createJobRequest,
  getAllJobsRequest,
  editJobRequest,
  fetchJobRequest,
  fetchJobCandidatesRequest,
  fetchCandidateOvertimeRequest,
  getUserOvertimeLimitsRequest,
  cancelJobRequest,
  confirmJobRequest,
  reportIncidentRequest,
  getJobsRequestByStatusDate,
  fetchJobsWithTransactions,
  multidayJobPostingRequest,
  ppbPostingRequest,
  fetchOpenJobRequest,
  fetchScheduledJobsRequest,
  fetchActionRequiredJobsRequest,
  fetchTodayJobsRequest,
  declineCounterOffer,
  fetchExpiredCounterOffersRequest,
  adjustHoursRequest,
  fetchCancellationReasonsRequest,
  setCancellationReasonsRequest,
  getLastPostedJobsRequest,
  getJobsRequest,
  getEligibleJobsRequest,

  // alerts
  getAlertsRequest,
  deleteAlertsRequest,
  deleteOneAlertRequest,
  getAllProfessionsRequest,
  getAllSpecializationsRequest,
  getUserEducationRequest,
  getOfficeEducationRequest,
  updateUserEducationRequest,
  getAllSoftwaresRequest,
  // messages
  getMessagesRequest,
  getMessagesByJobIdRequest,
  postMessageRequest,
  inviteAdminRequest,
  // dso
  getSelectedOfficeProfile,
  getDSOOfficeByIdRequest,
  // receipts
  fetchReceiptsRequest,
  fetchReceiptsV2Request,
  fetchReceiptsReportRequest,
  fetchReceiptsCSVReportV2Request,
  // invoice
  fetchInvoicesRequest,
  requestPaymentReport,
  // pp
  fetchAllPPJobsRequest,
  fetchPPApplicationByIdRequest,
  postPPActionnRequest,
  fetchPPApplicationsRequest,
  // referrals
  saveGiftEmailRequest,
  getReferralRequest,
  createReferralRequest,
  putReferralLinkRequest,
  // adjustmants
  fetchAdjustmentsRequest,
  changeStatusAdjustmentsRequest,
  // locum tenens dentist
  createLocumTenensDentistRequest,
  // notifications
  fetchNotificationSettingsRequest,
  updateNotificationSettingsRequest,
  // favorites
  fetchFavoritesProfessionalRequest,
  deleteFavoriteProfessionalRequest,
  addFavoriteProfessionalRequest,
  inviteFavoriteToJobRequest,
  inviteProfessionalsToJobsRequest,
  fetchRecentProfessionalsRequest,
  fetchRecommendedProfessionalsRequest,
  fetchOpenJobsForRecentProfessionalRequest,
  // invites
  fetchOfficeJobInvitesRequest,
  // partnership code
  fetchPartnershipOrganizationsRequest,
  verifyPartnershipCodeRequest,
  // metadata
  fetchMetadataRequest,
  // worker type
  saveWorkerClassificationRequest,
  getStateWorkerClassificationRequest,
  getWorkerClassificationManagement,
  saveChildOfficeWorkerClassification,
  saveStatusChildOfficeWorkerClassification,
  // Office Rating
  getOfficeReviewInfoRequest,
  // Child Offices
  getChildOfficesRequest,
  // Referral
  getReferralList,
  getReferralSummary,
  getReferralPayouts,
  confirmReferralPayout,
  linkReferralAccount,
  // Professional Profile
  fetchProfessionalProfile,
};
